import { useQuery } from '@tanstack/react-query';

import {
	ENTITY_NOT_FOUND_ERROR,
	ENTITY_STATUS_MAX_POLL_ATTEMPTS,
	ENTITY_STATUS_POLL_INTERVAL_MS
} from './useEntityStatusQuery.constants';
import {
	ENTITY_STATUS_DELETED,
	ENTITY_STATUS_FOUND,
	ENTITY_STATUS_LOADING,
	ENTITY_STATUS_SYNCING,
	EntityStatus,
	IUseEntityStatusQueryOptions,
	IUseEntityStatusQueryResult
} from './useEntityStatusQuery.types';

export const useEntityStatusQuery = <TData extends { name?: string }>({
	queryKey,
	queryFn
}: IUseEntityStatusQueryOptions<TData>): IUseEntityStatusQueryResult<TData> => {
	const query = useQuery({
		queryKey,
		queryFn: async () => {
			const data = await queryFn();
			if (!data?.name) {
				throw new Error(ENTITY_NOT_FOUND_ERROR);
			}
			return data as NonNullable<TData>;
		},
		retry: (failureCount, error) => {
			const isNotFound = error instanceof Error && error.message === ENTITY_NOT_FOUND_ERROR;
			return isNotFound && failureCount < ENTITY_STATUS_MAX_POLL_ATTEMPTS;
		},
		retryDelay: ENTITY_STATUS_POLL_INTERVAL_MS
	});

	const deriveStatus = (): EntityStatus => {
		if (query.isLoading) {
			return ENTITY_STATUS_LOADING;
		}
		if (query.data?.name) {
			return ENTITY_STATUS_FOUND;
		}
		if (query.failureCount >= ENTITY_STATUS_MAX_POLL_ATTEMPTS) {
			return ENTITY_STATUS_DELETED;
		}
		return ENTITY_STATUS_SYNCING;
	};

	return { query, status: deriveStatus() };
};
